import React, {
    useState,
    useContext,
    useRef,
    useEffect,
} from 'react';
import { FixedSizeList } from 'react-window';
import { GameItem } from '../GameItem/GameItem';
import { NoResult } from '../NoResult/NoResult';
import { AsgDictionaryContext } from '../../contexts';
import { RecommendedGames } from '../RecommendedGames/RecommendedGames';
import { RecentGames } from '../RecentGames/RecentGames';
import { ResultCount } from '../ResultCount/ResultCount';
import { PerfUtilService } from '../../../shared/src/services/util/perf-util.service';

export const ResultsContainer = ({
    gamesResult,
    textSearch,
    handleSearchOpenStatus,
    isLoggedIn,
}) => {
    const [hasRecentGames, setHasRecentGames] = useState(false);
    const [hasRecommendedGames, setHasRecommendedGames] = useState(false);
    const [searchResultWindowProps, setSearchResultWindowProps] = useState({});
    /**
     * used dictionary
     * @type {{}}
     */
    const dictionary = useContext(AsgDictionaryContext);
    const noGamesFound = dictionary.translate('noResults');
    const resultsFor = dictionary.translate('resultsFor');
    const resultsRef = useRef();
    const wrapRef = useRef();
    const resultsStartPointRef = useRef();
    const resultsOffsetTop = resultsRef?.current?.offsetTop;
    const resultsStyles = {
        '--offset-top': resultsOffsetTop ? resultsOffsetTop + 'px' : null,
    };

    const isGamesResult = !!gamesResult.length;
    const isSearchText = textSearch.length > 0;
    const showNoResults = !isGamesResult && isSearchText;
    const isSomethingToShow = isGamesResult
        || hasRecentGames
        || hasRecommendedGames
        || showNoResults;

    const baseWrapperClass = 'search-game-container-wrap';
    const wrapperClasses = [
        baseWrapperClass,
        isSomethingToShow ? '' : `${baseWrapperClass}--hidden`,
    ].filter(Boolean).join(' ');
    const searchItemHeight = 80;
    const totalResultsHeight = gamesResult.length * searchItemHeight;
    const maxWrapHeight = 573; // from desktop css
    const minResultsHeight = Math.min(totalResultsHeight, maxWrapHeight);

    const setSearchSizes = () => {
        const wrapHeight = wrapRef.current?.getBoundingClientRect().height || 0;
        const resultsStartPointOffsetTop = resultsStartPointRef.current?.offsetTop || 0;
        const height = Math.max(wrapHeight - resultsStartPointOffsetTop, minResultsHeight);
        setSearchResultWindowProps({
            height,
        });
    };

    const setSearchSizesThrottled = PerfUtilService.throttle(setSearchSizes, 300);

    // calculate search window size required for react-window
    useEffect(() => {
        setSearchSizes();

        window.addEventListener('resize', setSearchSizesThrottled);

        return () => {
            window.removeEventListener('resize', setSearchSizesThrottled);
        };
    }, [textSearch]);

    return (
        <div className={wrapperClasses}
            ref={wrapRef}>
            {
                isGamesResult
                && <ResultCount
                    resultsFor={resultsFor}
                    count={gamesResult.length}
                    terms={textSearch}
                />
            }

            <div ref={resultsStartPointRef}></div>

            {
                !isGamesResult
                && (<div
                    className="search-game-wrap__result-container"
                    ref={resultsRef}
                    style={resultsStyles}
                >
                    {
                        !isSearchText
                        && <RecentGames
                            handleSearchOpenStatus={handleSearchOpenStatus}
                            isLoggedIn={isLoggedIn}
                            setHasGames={setHasRecentGames}
                        />
                    }
                    {
                        !isSearchText
                        && <RecommendedGames
                            handleSearchOpenStatus={handleSearchOpenStatus}
                            setHasGames={setHasRecommendedGames}
                        />
                    }
                    {
                        showNoResults
                        && <NoResult
                            noGamesFound={noGamesFound}
                        />
                    }
                </div>)
            }

            {
                isGamesResult
                && (<FixedSizeList
                    className="search-game-wrap__result-container"
                    itemSize={80}
                    height={searchResultWindowProps.height || maxWrapHeight}
                    width="100%"
                    itemCount={gamesResult.length}
                    itemData={gamesResult}
                    direction="rtl"
                    ref={resultsRef}
                >
                    {({ data, index, style }) => {
                        const game = data[index];
                        return (
                            <div
                                key={game.id}
                                style={style}
                            >
                                <GameItem
                                    onOpenGame={() => handleSearchOpenStatus(data)}
                                    data={game}
                                />
                            </div>
                        );
                    }}
                </FixedSizeList>)
            }
        </div>
    );
};
